import { motion } from "framer-motion";
import {
  Shield,
  Lock,
  Eye,
  FileText,
  Phone,
  Mail,
  Cookie,
  UserCheck,
} from "lucide-react";

const sections = [
  {
    id: "information",
    icon: Eye,
    title: "Information We Collect",
    content: [
      {
        subtitle: "Personal Information",
        text: "When you browse our dealership website, inquire about vehicles, or complete a purchase, we collect personal details you provide such as your full name, residential address, email address, and telephone number. This information helps us serve you better and process your requests efficiently.",
      },
      {
        subtitle: "Technical Information",
        text: "As you navigate through our website, we automatically receive your device's internet protocol (IP) address. This helps us understand your browser type, operating system, and device specifications to optimize your browsing experience and ensure website compatibility.",
      },
      {
        subtitle: "Marketing Communications",
        text: "With your explicit consent, we may send you promotional emails about our latest vehicle inventory, special financing offers, service promotions, and dealership updates. You can opt-out of these communications at any time.",
      },
    ],
  },
  {
    id: "consent",
    icon: UserCheck,
    title: "Your Consent",
    content: [
      {
        subtitle: "How We Obtain Consent",
        text: "When you provide us with personal information to complete a vehicle inquiry, financing application, or service request, we imply that you consent to our collecting and using it for that specific purpose only. For secondary purposes like marketing, we will ask for your explicit consent.",
      },
      {
        subtitle: "Withdrawing Consent",
        text: "If you change your mind after opting in, you may withdraw your consent for us to contact you or continue collecting your information at any time. Simply contact us at (416) 555-7755 or email us at privacy@bigboysmotorclub.com.",
      },
    ],
  },
  {
    id: "disclosure",
    icon: FileText,
    title: "Information Disclosure",
    content: [
      {
        subtitle: "Legal Requirements",
        text: "We may disclose your personal information if required by law, court order, or governmental regulation. We will always attempt to notify you before such disclosure unless prohibited by law.",
      },
      {
        subtitle: "Payment Security",
        text: "When you use your credit card for vehicle reservations, parts orders, or service payments, your data is encrypted using Payment Card Industry Data Security Standard (PCI-DSS). Transaction data is stored only as long as necessary to complete your purchase, after which it is securely deleted.",
      },
    ],
  },
  {
    id: "third-party",
    icon: Shield,
    title: "Third-Party Services",
    content: [
      {
        subtitle: "Service Providers",
        text: "Our third-party service providers collect, use, and disclose your information only to the extent necessary to perform services on our behalf, such as payment processing, credit checks, and vehicle delivery coordination.",
      },
      {
        subtitle: "External Links",
        text: "Our website may contain links to external sites. Once you leave our website, you are no longer governed by this Privacy Policy. We encourage you to review the privacy policies of any third-party websites you visit.",
      },
    ],
  },
  {
    id: "security",
    icon: Lock,
    title: "Data Security",
    content: [
      {
        subtitle: "Protection Measures",
        text: "We implement industry-standard security measures to protect your personal information from unauthorized access, disclosure, alteration, or destruction. This includes encryption, secure servers, and regular security audits.",
      },
      {
        subtitle: "No Absolute Guarantee",
        text: "While we strive to use commercially acceptable means to protect your personal information, no method of transmission over the Internet or electronic storage is 100% secure. We cannot guarantee absolute security.",
      },
    ],
  },
  {
    id: "cookies",
    icon: Cookie,
    title: "Cookies Policy",
    content: [
      {
        subtitle: "What We Use",
        text: "Our website uses cookies to enhance your browsing experience. These include session cookies for maintaining your login state, analytics cookies to understand website traffic, and preference cookies to remember your settings.",
      },
      {
        subtitle: "Managing Cookies",
        text: "You can choose to disable cookies through your browser settings. However, this may affect your ability to use certain features of our website. By continuing to use our site, you consent to our use of cookies.",
      },
    ],
  },
];

export default function PrivacyPolicy() {
  return (
    <main className="w-full overflow-x-hidden bg-white">
      {/* Hero Section */}
      <section className="relative pt-28 pb-16 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900">
        <div className="absolute inset-0 opacity-10">
          <div
            className="absolute inset-0"
            style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
            }}
          />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-brand-red/20 rounded-full mb-6">
              <Shield className="w-5 h-5 text-brand-red" />
              <span className="text-brand-red text-sm font-medium">
                Your Privacy Matters
              </span>
            </div>

            <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
              Privacy <span className="text-brand-red">Policy</span>
            </h1>

            <p className="text-gray-400 text-lg max-w-2xl mx-auto">
              At Big Boys Motor Club, we are committed to protecting your
              personal information and being transparent about how we use it.
            </p>

            <div className="w-24 h-1 bg-gradient-to-r from-brand-red to-brand-gold mx-auto mt-8 rounded-full" />
          </motion.div>
        </div>
      </section>

      {/* Last Updated Banner */}
      <section className="py-4 bg-gray-50 border-b border-gray-200">
        {/* <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-center text-gray-600 text-sm">
            Last Updated:{" "}
            <span className="font-semibold text-gray-900">
              February 5, 2026
            </span>
          </p>
        </div> */}
      </section>

      {/* Content Sections */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-16">
            {sections.map((section, index) => (
              <motion.div
                key={section.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-100px" }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="scroll-mt-32"
                id={section.id}
              >
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-12 h-12 bg-brand-red/10 rounded-xl flex items-center justify-center flex-shrink-0">
                    <section.icon className="w-6 h-6 text-brand-red" />
                  </div>
                  <h2 className="font-serif text-2xl md:text-3xl font-bold text-gray-900">
                    {section.title}
                  </h2>
                </div>

                <div className="space-y-6 pl-16">
                  {section.content.map((item, idx) => (
                    <div key={idx} className="bg-gray-50 rounded-xl p-6">
                      <h3 className="font-semibold text-lg text-gray-900 mb-3">
                        {item.subtitle}
                      </h3>
                      <p className="text-gray-600 leading-relaxed">
                        {item.text}
                      </p>
                    </div>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-16 bg-gray-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <h2 className="font-serif text-3xl font-bold text-white mb-4">
              Questions About Your Privacy?
            </h2>
            <p className="text-gray-400 mb-8">
              If you have any questions or concerns about our privacy practices,
              please don't hesitate to reach out to us.
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-6">
              <a
                href="tel:+16043588081"
                className="flex items-center gap-3 px-6 py-3 bg-brand-red text-white rounded-full hover:bg-red-700 transition-colors"
              >
                <Phone className="w-5 h-5" />
                (604) 358-8081
              </a>
              <a
                href="mailto:privacy@bigboysmotorclub.com"
                className="flex items-center gap-3 px-6 py-3 bg-white/10 text-white border border-white/20 rounded-full hover:bg-white/20 transition-colors"
              >
                <Mail className="w-5 h-5" />
                privacy@bigboysmotorclub.com
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Policy Changes Notice */}
      <section className="py-8 bg-brand-red/5 border-t border-brand-red/10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 bg-brand-red/10 rounded-lg flex items-center justify-center flex-shrink-0 mt-1">
              <FileText className="w-5 h-5 text-brand-red" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 mb-2">
                Policy Updates
              </h3>
              <p className="text-gray-600 text-sm leading-relaxed">
                We reserve the right to modify this privacy policy at any time.
                Changes take effect immediately upon posting to this page. For
                significant changes, we will notify you via email or through a
                prominent notice on our website.
              </p>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
